import { fromLocalDateTime, startOfNextDay, toLocalParts } from "./time";
import type { EpochSeconds, LocalDate } from "./time";

/*
 * Positions of the day band (F-43, ADR-0159): a local day from 06:00 to 18:00, widened to the full
 * hours of its intervals. Positions are percentages of the band's width, so the band scales with
 * its container.
 */

/** Kind of an interval on the band: working time, break time, or presence without the split. */
export type DayBandKind = "Work" | "Break" | "Presence";

/** An interval on the band, in epoch seconds. */
export interface DayBandInterval {
  readonly kind: DayBandKind;
  readonly start: EpochSeconds;
  readonly end: EpochSeconds;
}

/** An interval positioned on the band. */
export interface DayBandSegment {
  readonly kind: DayBandKind;
  /** Left edge in percent of the band's width, from 0 to 100. */
  readonly left: number;
  /** Width in percent of the band's width, from 0 to 100. */
  readonly width: number;
}

/** The hours a band shows, with the instants at both of its ends. */
export interface DayBandWindow {
  /** First full hour on the band, from 0 to 23. */
  readonly startHour: number;
  /** Hour at which the band ends, from 1 to 24. */
  readonly endHour: number;
  /** Instant at the start of the first hour. */
  readonly start: EpochSeconds;
  /** Instant at the end of the last hour. */
  readonly end: EpochSeconds;
}

/** First hour of the band (06:00, design Option B). */
export const dayBandStartHour = 6;
/** Hour at which the band ends (18:00, design Option B). */
export const dayBandEndHour = 18;

const hoursPerDay = 24;
const percent = 100;

/**
 * Hours of the band of a local day: from 06:00 to 18:00, widened to the full hours of every
 * interval, so that a longer or a night shift stays on the band. Intervals outside the window are
 * cut at its ends by {@link dayBandSegments}.
 *
 * @param date - Local date of the band.
 * @param intervals - Intervals of the day.
 * @param timeZone - IANA time zone of the day.
 * @returns The window.
 */
export function dayBandWindow(
  date: LocalDate,
  intervals: readonly DayBandInterval[],
  timeZone: string,
): DayBandWindow {
  let startHour = dayBandStartHour;
  let endHour = dayBandEndHour;

  for (const interval of intervals) {
    const first = toLocalParts(interval.start, timeZone);
    const last = toLocalParts(interval.end, timeZone);
    const lastHour = last.minute > 0 || last.second > 0 ? last.hour + 1 : last.hour;

    startHour = Math.min(startHour, first.hour);
    endHour = Math.min(Math.max(endHour, lastHour), hoursPerDay);
  }

  const start = fromLocalDateTime(date, hourText(startHour), timeZone);
  const end =
    endHour === hoursPerDay
      ? startOfNextDay(date, timeZone)
      : fromLocalDateTime(date, hourText(endHour), timeZone);

  return { startHour, endHour, start, end };
}

/**
 * The intervals positioned on the band. An interval is cut at the window's ends, and an interval
 * that is empty inside the window is left out.
 *
 * @param intervals - Intervals of the day.
 * @param window - The window of the band.
 * @returns The segments in the order of the intervals.
 */
export function dayBandSegments(
  intervals: readonly DayBandInterval[],
  window: DayBandWindow,
): DayBandSegment[] {
  const span = window.end - window.start;
  const segments: DayBandSegment[] = [];

  for (const interval of intervals) {
    const from = clamp(interval.start, window.start, window.end);
    const to = clamp(interval.end, window.start, window.end);
    const left = ((from - window.start) / span) * percent;
    const width = ((to - from) / span) * percent;

    if (width > 0) {
      segments.push({ kind: interval.kind, left, width });
    }
  }

  return segments;
}

/**
 * Position of an instant on the band, in percent of its width. An instant outside the window is
 * placed at the nearest end.
 *
 * @param instant - The instant, for example the current time.
 * @param window - The window of the band.
 * @returns Percent from 0 to 100.
 */
export function dayBandPosition(instant: EpochSeconds, window: DayBandWindow): number {
  const span = window.end - window.start;
  const offset = clamp(instant, window.start, window.end) - window.start;
  const position = (offset / span) * percent;

  return position;
}

/**
 * Restricts a value to an interval.
 *
 * @param value - The value.
 * @param min - Lower bound.
 * @param max - Upper bound.
 * @returns The value inside the bounds.
 */
function clamp(value: number, min: number, max: number): number {
  const bounded = Math.min(Math.max(value, min), max);

  return bounded;
}

/**
 * Local time of a full hour as `HH:00`.
 *
 * @param hour - Hour from 0 to 23.
 * @returns The time text.
 */
function hourText(hour: number): string {
  const text = `${String(hour).padStart(2, "0")}:00`;

  return text;
}
